import styled from 'styled-components'

import useReduxSelector from 'hooks/useReduxSelector'
import typeStyle from 'styles/typeStyle'
import Avatar from '../../../components/display/Avatar'
import LazyImage from '../../../components/display/LazyImage'
import UnreadIndicator from '../../../inbox/UnreadIndicator'

import {
  Divider,
  NavLinkOrAnchor,
  NavLink,
  NavLinkAnchor,
  NavLinkLoading,
} from '../common'

import IncompleteBooking from '../IncompleteBooking'

const Name = styled.p`
  ${typeStyle('-ds-font-lead')}
  margin-bottom: 8px;
`

const ProfileCTA = styled.p`
  ${typeStyle('-ds-font-lead')}
  color: ${({ theme }) => theme['-ds-core-400'].value};
  margin-bottom: 0;
`

const ProfileAvatar = styled.div`
  ${typeStyle('-ds-font-lead')}
  display: flex;
  align-items: center;
  margin-bottom: 16px;

  ${Avatar} {
    width: 64px;
    height: 64px;
    display: inline-block;
    vertical-align: middle;
    margin-right: 16px;
  }

  p {
    line-height: 1.2;
  }

  a {
    text-decoration: 'none';

    &:visited {
      text-decoration: 'none';
    }
  }
`

const LinkList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;

  ${NavLink}, ${NavLinkAnchor} {
    ${typeStyle('-ds-font-lead')}
    line-height: 44px;
  }

  ${NavLinkLoading} {
    height: 44px;
  }

  & > *:not(:last-child) {
    margin-bottom: 8px;
  }
`

export const LoggedInLinks = () => {
  const {
    avatar_url: avatarUrl,
    unread_conversations_count: unreadCount,
    fullname,
    host,
    storyteller,
    admin,
    eligible_for_referrals: eligibleForReferrals,
  } = useReduxSelector((state) => state?.auth?.currentUser)

  return (
    <LinkList>
      <li>
        <IncompleteBooking />
      </li>

      <li>
        <ProfileAvatar>
          <Avatar>
            <LazyImage data-src={avatarUrl} alt={fullname} />
          </Avatar>
          <NavLinkOrAnchor to="/my/profile/edit">
            <Name>{fullname}</Name>
            <ProfileCTA>Edit Profile</ProfileCTA>
          </NavLinkOrAnchor>
        </ProfileAvatar>
      </li>
      <li>
        <NavLinkAnchor href="/">Home</NavLinkAnchor>
      </li>

      {storyteller && (
        <li>
          <NavLinkOrAnchor to="/my/assignments">
            Story Assignments
          </NavLinkOrAnchor>
        </li>
      )}

      {host && (
        <li>
          <NavLinkOrAnchor to="/my/listings">My Listings</NavLinkOrAnchor>
        </li>
      )}

      <li>
        <NavLinkOrAnchor to="/my/bucketlists">Bucket Lists</NavLinkOrAnchor>
      </li>
      <li>
        <NavLinkOrAnchor to="/my/bookings">Booked Escapes</NavLinkOrAnchor>
      </li>
      {eligibleForReferrals && (
        <li>
          <NavLinkOrAnchor to="/my/referrals">Refer a Friend</NavLinkOrAnchor>
        </li>
      )}
      <li>
        <NavLinkOrAnchor to="/my/inbox">
          Inbox
          <UnreadIndicator unreadCount={unreadCount} />
        </NavLinkOrAnchor>
      </li>
      <li>
        <NavLinkOrAnchor to="/my/account">Account</NavLinkOrAnchor>
      </li>

      <Divider />

      <li>
        <NavLinkOrAnchor to="/vouchers">Gift Vouchers</NavLinkOrAnchor>
      </li>
      <li>
        <NavLinkOrAnchor to="/list-with-us">Become a Host</NavLinkOrAnchor>
      </li>
      <li>
        <NavLinkAnchor
          href="https://help.riparide.com"
          rel="noopener noreferrer"
          target="_blank"
        >
          Help and FAQs
        </NavLinkAnchor>
      </li>
      <li>
        <NavLinkOrAnchor to="/about">About</NavLinkOrAnchor>
      </li>

      <Divider />

      <li>
        <NavLinkAnchor href="/users/sign_out">Log Out</NavLinkAnchor>
      </li>

      {admin && (
        <li>
          <NavLinkAnchor href="/admin">Admin</NavLinkAnchor>
        </li>
      )}
    </LinkList>
  )
}

export const LoggedOutLinks = () => {
  return (
    <LinkList>
      <li>
        <NavLinkAnchor href="/">Home</NavLinkAnchor>
      </li>
      <li>
        <NavLinkOrAnchor to="/listings?type=accommodation">
          Book an Escape
        </NavLinkOrAnchor>
      </li>

      <Divider />

      <li>
        <NavLinkAnchor href="/about">About Riparide</NavLinkAnchor>
      </li>
      <li>
        <NavLinkAnchor href="/vouchers">Buy a Gift Voucher</NavLinkAnchor>
      </li>
      <li>
        <NavLinkOrAnchor to="/list-with-us">Become a Host</NavLinkOrAnchor>
      </li>
      <li>
        <NavLinkAnchor
          href="https://help.riparide.com"
          rel="noopener noreferrer"
          target="_blank"
        >
          Help and FAQs
        </NavLinkAnchor>
      </li>

      <Divider />

      <li>
        <NavLinkAnchor href="/signup">Sign Up</NavLinkAnchor>
      </li>
      <li>
        <NavLinkAnchor href="/login">Login</NavLinkAnchor>
      </li>
    </LinkList>
  )
}

export const LinksLoadingSkeleton = () => (
  <LinkList>
    <li>
      <NavLinkLoading />
    </li>
    <li>
      <NavLinkLoading />
    </li>
    <li>
      <NavLinkLoading />
    </li>
    <li>
      <NavLinkLoading />
    </li>
    <li>
      <NavLinkLoading />
    </li>
    <li>
      <NavLinkLoading />
    </li>
    <li>
      <NavLinkLoading />
    </li>
  </LinkList>
)
